import React from 'react';
import { Container, Row, Col, Card, ProgressBar, Button, Badge } from 'react-bootstrap';
import { Award, Gift, TrendingUp, Users, CheckCircle, Lock, Star, Heart } from 'lucide-react';
import '../../../assets/css/donor/dashboard/MyLevel.css';
import DonorLayout from '../../../layouts/donor/DonorLayout';

const MyLevels = () => {
  // Current donor data
  const donorData = {
    currentLevel: 'Gold',
    currentLevelIcon: '🥇',
    totalDonated: 135000,
    nextLevel: 'Platinum',
    nextLevelAmount: 500000,
    progressPercent: 65,
    remainingAmount: 365000
  };

  // Rewards data
  const unlockedRewards = [
    { id: 1, name: 'Digital Badge', unlocked: true },
    { id: 2, name: 'Certificate', unlocked: true },
    { id: 3, name: 'Premium Package', unlocked: false }
  ];

  // Level journey data
  const levelJourney = [
    {
      id: 1,
      name: 'BRONZE',
      icon: '🥉',
      minAmount: 10000,
      maxAmount: 49000,
      reward: 'Badge',
      status: 'Completed',
      completed: true,
      current: false
    },
    {
      id: 2,
      name: 'SILVER',
      icon: '🥈',
      minAmount: 50000,
      maxAmount: 99000,
      reward: 'Certificate',
      status: 'Completed',
      completed: true,
      current: false
    },
    {
      id: 3,
      name: 'GOLD',
      icon: '🥇',
      minAmount: 100000,
      maxAmount: 499000,
      reward: 'Cert + Badge',
      status: 'Active',
      completed: false,
      current: true
    },
    {
      id: 4,
      name: 'PLATINUM',
      icon: '💎',
      minAmount: 500000,
      maxAmount: null,
      reward: 'Premium Package',
      status: 'Locked',
      completed: false,
      current: false
    }
  ];

  // Impact summary
  const impactSummary = {
    totalDonations: 135000,
    casesSupported: 12,
    studentsHelped: 12,
    currentRank: 'Gold Donor'
  };

  return (
    <DonorLayout>
      <div className="my-levels-container">
        <Container fluid>
          {/* Page Header */}
          <div className="my-levels-page-header">
            <h2 className="my-levels-page-title">MY LEVELS</h2>
            <p className="my-levels-page-subtitle">
              Track your donation progress & unlock rewards
            </p>
          </div>
          {/* Donation Impact Summary */}
          <Row className="mb-4">
            <Col>
              <Card className="my-levels-impact-card">
                <Card.Body>
                  <h4 className="my-levels-section-title">
                    <TrendingUp size={22} className="me-2" />
                    DONATION IMPACT SUMMARY
                  </h4>
                  
                  <Row className="g-3">
                    <Col md={3} className="d-flex">
                      <div className="my-levels-impact-box w-100">
                        <div className="my-levels-impact-icon my-levels-impact-icon-primary">
                          <TrendingUp size={24} />
                        </div>
                        <div className="my-levels-impact-content">
                          <p className="my-levels-impact-label">Total Donations</p>
                          <h4 className="my-levels-impact-value">
                            PKR {impactSummary.totalDonations.toLocaleString()}
                          </h4>
                        </div>
                      </div>
                    </Col>

                    <Col md={3} className="d-flex">
                      <div className="my-levels-impact-box w-100">
                        <div className="my-levels-impact-icon my-levels-impact-icon-success">
                          <CheckCircle size={24} />
                        </div>
                        <div className="my-levels-impact-content">
                          <p className="my-levels-impact-label">Total Cases Supported</p>
                          <h4 className="my-levels-impact-value">{impactSummary.casesSupported}</h4>
                        </div>
                      </div>
                    </Col>

                    <Col md={3} className="d-flex">
                      <div className="my-levels-impact-box w-100">
                        <div className="my-levels-impact-icon my-levels-impact-icon-orange">
                          <Users size={24} />
                        </div>
                        <div className="my-levels-impact-content">
                          <p className="my-levels-impact-label">Students Helped</p>
                          <h4 className="my-levels-impact-value">{impactSummary.studentsHelped}</h4>
                        </div>
                      </div>
                    </Col>

                    <Col md={3} className="d-flex">
                      <div className="my-levels-impact-box w-100">
                        <div className="my-levels-impact-icon my-levels-impact-icon-gold">
                          <Award size={24} />
                        </div>
                        <div className="my-levels-impact-content">
                          <p className="my-levels-impact-label">Current Rank</p>
                          <h4 className="my-levels-impact-value">{impactSummary.currentRank}</h4>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Current Level Section */}
          <Row className="mb-4">
            <Col>
              <Card className="my-levels-current-card">
                <Card.Body>
                  <h4 className="my-levels-section-title">CURRENT LEVEL</h4>
                  
                  <Row className="align-items-center">
                    <Col md={2} className="text-center">
                      <div className="my-levels-icon-wrapper">
                        <span className="my-levels-icon">{donorData.currentLevelIcon}</span>
                      </div>
                    </Col>
                    
                    <Col md={10}>
                      <div className="my-levels-current-info">
                        <h3 className="my-levels-current-name">
                          {donorData.currentLevel} Donor {donorData.currentLevelIcon}
                        </h3>
                        <p className="my-levels-current-donated">
                          Total Donated: <span>PKR {donorData.totalDonated.toLocaleString()}</span>
                        </p>
                      </div>

                      <div className="my-levels-progress-section">
                        <div className="my-levels-progress-header">
                          <span className="my-levels-progress-label">
                            Progress to Next Level ({donorData.nextLevel})
                          </span>
                          <span className="my-levels-progress-percent">
                            {donorData.progressPercent}%
                          </span>
                        </div>
                        <ProgressBar 
                          now={donorData.progressPercent} 
                          className="my-levels-progress-bar"
                        />
                        <p className="my-levels-progress-text">
                          PKR {donorData.remainingAmount.toLocaleString()} more to reach {donorData.nextLevel}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Rewards & Become Platinum Row */}
          <Row className="mb-4 g-4">
            {/* Rewards Unlocked */}
            <Col lg={7} className="d-flex">
              <Card className="my-levels-rewards-card w-100">
                <Card.Body>
                  <h4 className="my-levels-card-title">
                    <Gift size={22} className="me-2" />
                    REWARDS UNLOCKED 🎁
                  </h4>
                  
                  <div className="my-levels-rewards-list">
                    {unlockedRewards.map((reward) => (
                      <div 
                        key={reward.id} 
                        className={`my-levels-reward-item ${reward.unlocked ? 'unlocked' : 'locked'}`}
                      >
                        {reward.unlocked ? (
                          <CheckCircle size={20} className="my-levels-reward-check" />
                        ) : (
                          <span className="my-levels-reward-pending">⏳</span>
                        )}
                        <span className="my-levels-reward-name">{reward.name}</span>
                        {!reward.unlocked && (
                          <span className="my-levels-locked-text">(Locked)</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Become Platinum */}
            <Col lg={5} className="d-flex">
              <Card className="my-levels-platinum-card w-100">
                <Card.Body>
                  <h4 className="my-levels-card-title">
                    <Star size={22} className="me-2" />
                    BECOME PLATINUM 🌟
                  </h4>
                  
                  <div className="my-levels-platinum-content">
                    <p className="my-levels-platinum-text">
                      Donate <strong>PKR {donorData.remainingAmount.toLocaleString()}</strong> more to unlock premium benefits & recognition
                    </p>
                    
                    <Button className="my-levels-donate-btn">
                      <Heart size={18} />
                      Donate Now
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Level Journey Section */}
          <Row className="mb-4">
            <Col>
              <Card className="my-levels-journey-card">
                <Card.Body>
                  <h4 className="my-levels-section-title">YOUR LEVEL JOURNEY</h4>
                  
                  <div className="my-levels-journey-list">
                    {levelJourney.map((level) => (
                      <div 
                        key={level.id}
                        className={`my-levels-journey-item ${
                          level.completed ? 'completed' : 
                          level.current ? 'current' : 'locked'
                        }`}
                      >
                        <div className="my-levels-journey-status">
                          {level.completed && (
                            <CheckCircle size={24} className="my-levels-journey-check" />
                          )}
                          {level.current && (
                            <div className="my-levels-journey-current-dot">●</div>
                          )}
                          {!level.completed && !level.current && (
                            <Lock size={24} className="my-levels-journey-lock" />
                          )}
                        </div>

                        <div className="my-levels-journey-content">
                          <div className="my-levels-journey-header">
                            <h5 className="my-levels-journey-name">
                              {level.icon} {level.name}
                              {level.current && (
                                <Badge bg="warning" className="my-levels-current-badge ms-2">
                                  CURRENT
                                </Badge>
                              )}
                            </h5>
                          </div>

                          <div className="my-levels-journey-details">
                            <div className="my-levels-journey-row">
                              <span className="my-levels-journey-label">Amount Range:</span>
                              <span className="my-levels-journey-value">
                                PKR {level.minAmount.toLocaleString()} – {level.maxAmount ? `${level.maxAmount.toLocaleString()}` : '∞'}
                              </span>
                            </div>
                            <div className="my-levels-journey-row">
                              <span className="my-levels-journey-label">Reward:</span>
                              <span className="my-levels-journey-value">{level.reward}</span>
                            </div>
                            <div className="my-levels-journey-row">
                              <span className="my-levels-journey-label">Status:</span>
                              <Badge 
                                bg={
                                  level.completed ? 'success' : 
                                  level.current ? 'warning' : 'secondary'
                                }
                                className="my-levels-status-badge"
                              >
                                {level.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          
        </Container>
      </div>
    </DonorLayout>
  );
};

export default MyLevels;